import React, { useEffect, useRef, useState } from "react"
import _ from "lodash"
// PR
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import {ContextMenu} from "primereact/contextmenu"
// Components
import CarouselModal, { CarouselData } from "components/table/carousel/carousel-modal"
import CarouselTrigger from "components/table/carousel/carousel-trigger"
import { field as _field } from "components/table/lib/column-field"
import SearchHeader from "components/table/search-header"
import {
  TableAutomation,
  focusFirstCellByRowIndex,
  focusCellByContent,
  focusCellByDataId,
  pageToFirst,
  getPagingInfo,
  getFocusedRowIndex,
  unfocusAllCells } from "components/table/table-automation"
import { TextEdit, DropdownEdit, CheckboxInput } from "components/table/inputs"
import { DuplicateModal } from "components/modal/duplicate-confirmation"
import { DropdownField } from "components/table/display"
import ConfirmationModal from "components/modal/confirmation-modal"
// Controls
import Controls from "components/departmental-line-items/controls/"
// Data
import automationService from "data/automation"
import carouselService from "data/carousel"
import lineItemsService from "data/line-items"
import detailAccountService from "data/detailaccount"
import summaryAccountService from "data/summaryaccount"
import keyService, * as chord from "data/key"
import propertyService from "data/property"
import notificationService from "data/notification"
import { alphanum } from "components/table/lib/sort"
import { handleReferences } from "data/lib/handle-references"
import { copy, paste, canPaste } from "data/lib/copy-paste"
// Styles
import { DepartmentalLineItemsTable_s } from "components/departmental-line-items/departmental-line-items-styles"

type ContextRow = {
  lineitemid: number
  propertyid: number
  code: string
  systeminput?: string
}

type ILineItemsTable = {
  tableId: string
  data: object[]
  defaults: object
  inflationData: object[]
  areaMeasuresData: object[]
  lineItemsData: object[]
  systemLineItemsData: object[]
  rentrollData: object[]
  type: string
  chartId: number
}


const ChartDepartmentalLineItemsTable = (props: ILineItemsTable) => {
  const usePrevious = (value) =>{
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  }

  const findKinRecord = (lineItem) => {
    let kinRecord = {}
    if(lineItem.parentlineitemid) {
      kinRecord = _.find(props.data, r => r.lineitemid === lineItem.parentlineitemid)
    } else {
      kinRecord = _.find(props.data, r => r.parentlineitemid === lineItem.lineitemid)
    }
    return kinRecord
  }

  const traceMenuItem = {
    label: 'Trace', icon: 'pi pi-fw pi-search', command: e=> {
      const refreshFn = () => lineItemsService.fetchLineItems(contextRow.propertyid)
      handleReferences('lineitem', contextRow.lineitemid, {refreshFn});
    }
  }

  const nonSystemMenu = [
    {label: 'Copy Cell', icon: 'pi pi-fw pi-copy', command: e => { if(contextRow.systeminput === "systemImmutable") return;
      copy(contextCell, contextRow)
    }},
    {label: 'Duplicate Row', icon: 'pi pi-fw pi-clone', command: e => { if(contextRow.systeminput) return;
      lineItemsService.copy(contextRow.propertyid, contextRow.lineitemid, contextRow.code)
    }},
    {label: 'Duplicate Row n Times', icon: 'pi pi-fw pi-clone', command: async e => { if(contextRow.systeminput) return;
      showDuplicateConfirmation(async (n) => {
        const lineitems = await lineItemsService.copies(contextRow.propertyid, contextRow.lineitemid, n)
      })
    }},
    {label: 'Delete Row', icon: 'pi pi-fw pi-trash', command: e => { if(contextRow.systeminput) return;
      const pager         = getPagingInfo(props.tableId);
      const currentRowNum = getFocusedRowIndex() + 1
      const currentRecordIndex = (pager.currentPage > 1 ? (pager.currentPage - 1) * pager.pageSize : 0) + currentRowNum - 1
      showDeleteConfirmation(contextRow, currentRecordIndex + 1)
    }},
    traceMenuItem
  ]

  const systemMenu = [{...traceMenuItem}]

  const [carouselData, updateCarouselData] = useState<CarouselData|null>(null);
  const [searchFilter, setSearchFilter] = useState<string>("")
  const [contextRow, setContextRow] = useState<ContextRow>({lineitemid: 0, propertyid: 0, code: ''})
  const [contextCell, setContextCell] = useState<object|null>(null)
  const [deleteConfirmation, setDeleteConfirmation] = useState<JSX.Element | null>(null)
  const [duplicateConfirmation, setDuplicateConfirmation] = useState<JSX.Element | null>(null)
  const [details, setDetails] = useState<object[]|null>([])
  const [summaries, setSummaries] = useState<object[]|null>([])
  const [showOverwriteModal, setShowOverwriteModal] = useState<boolean>(false)
  const [rowTypeCache, setRowTypeCache] = useState<string>("")
  const [lineItemCache, setLineItemCache] = useState<object>({})
  const [detailAccountCache, setDetailAccountCache] = useState<object>({})
  const menuRef = useRef<ContextMenu | null>(null)
  const [menu, setMenu] = useState<[]|object[]>(nonSystemMenu)
  const prevSearch = usePrevious(searchFilter);
  const tableRef = useRef() as React.MutableRefObject<DataTable>
  const inflationData: object[] = props.inflationData
  const areaMeasuresData: object[] = props.areaMeasuresData
  const lineItemsData: object[] = props.lineItemsData
  const systemLineItemsData: object[] = props.systemLineItemsData
  const rentrollData: object[] = props.rentrollData
  const defaults: object = props.defaults
  const field = _field('lineitem')
  const propType = "hospitality"
  const LineItemTypeOptions =
    [
      {value: "hosp_dept_revenue", label: "Dept. Revenue", prefix: "Dept. Revenue", summaryAccountCategory: "Revenue"},
      {value: "hosp_dept_expense", label: "Dept. Expense", prefix: "Dept. Expense", summaryAccountCategory: "Operating Expenses"},
      {value: "system", label: "System", prefix: "System"}
    ]

  let detail_controls = {
    basic: {control: Controls.Amount, name: "Amount"},
    amtArea: {control: Controls.AmtPerArea, name: "Amt/Area"},
    percentLineItem: {control: Controls.PercentLine, name: "% of Line"},
    detailed: {control: Controls.Detailed, name: "Detailed"},
    detailedPerArea: {control: Controls.DetailedPerArea, name: "Detailed Per Area"},
    detailedPercentLineItem: {control: Controls.DetailedPercentLine, name: "Detailed % of Line"},
    prop13Tax: {control: Controls.Prop13Tax, name: "Prop 13 Tax"}
  }

  let detail_hovers = {
    basic: [{key: 'amt', label: 'Amt'}, {key: 'amttype', label: 'Amt Type'}, {key: ['inflationdetail', 'inflationNamedItemId'], label: 'Inflation'}],
    amtArea: [{key: 'amt', label: 'Amt'}, {key: 'amttype', label: 'Amt Type'}, {key: ['inflationdetail', 'inflationNamedItemId'], label: 'Inflation'}, {key: ['areameasuredetail', 'areaid'], label: 'Area Measure'}],
    percentLineItem: {multiple: [{key: 'lineItemId', label: 'Account #'}, {key: 'percent', label: 'Percent'}]},
    lineItemDetailed: {single: [{key: ['inflationdetail', 'inflationNamedItemId'], label: 'Inflation'}]},
    detailedPerArea: {single: [{key: ['inflationdetail', 'inflationNamedItemId'], label: 'Inflation'}, {key: ['areaMeasureNamedItemId'], label: 'Area Measure'}]},
    detailedPercentLineItem: {single: [{key: 'lineItemId', label: 'Account #'}]},
    prop13Tax: [{key: ['lineitemdetail', 'year1AmtType'], label: 'Year 1 Amt Type'}, {key: ['lineitemdetail', 'year1Amt'], label: 'Amt'}, {key: ['lineitemdetail', 'percent'], label: '% of Value'}, {key: ['lineitemdetail', 'valuationScenario'], label: 'Valuation Scenario'}, {key: ['lineitemdetail', 'inflationNamedItemId'], label: 'Inflation'}]
  }

  const unfilteredDetailAccountOptions = _(details) // for displaying on initial load, selection will be filtered when editor is opened
      .map((d) => { return {label: `${d.accountnumber} - ${d.description}`, value: d.detailaccountid} })
      .value()

  const filterDetailAccounts = (rowData) => {
    const lineItemType = _.get(_.find(LineItemTypeOptions, (o) => o.value === _.get(rowData, "type")), "summaryAccountCategory")
    const filteredSummaries = _(summaries)
      .filter((s) => s.accountcategory === lineItemType)
      .filter((s) => s.propertytype.map((pt) => pt.toLowerCase())
      .includes(propType.toLowerCase()))
      .map((s) => s.summaryaccountid)
      .value()

    const detailAccountOptions = _(details)
      .filter((d) => filteredSummaries.includes(d.summaryaccountid))
      .map((d) => { return {label: `${d.accountnumber} - ${d.description}`, value: d.detailaccountid} })
      .value()

    return detailAccountOptions
  }

  useEffect(() => {
    const carouselSubscription = carouselService.stream().subscribe(data => {
      updateCarouselData(data);
    })

    const detailAccountSubscription = detailAccountService.stream().subscribe((data) => {
      if (data.state === "ok"){
        setDetails(data.value)
      }
    })

    const summaryAccountSubscription = summaryAccountService.stream().subscribe((data) => {
      if (data.state === "ok"){
        setSummaries(data.value)
      }
    })

    const lineItemsEventSubscription = lineItemsService.eventStream().subscribe(e => {
      switch(true) {
        case e.type === "LINEITEM_CREATED":
          setTimeout(() => pageToFirst(props.tableId), 1)
          setTimeout(() => {
            focusFirstCellByRowIndex(props.tableId, 0)
          }, 500)
          break
        case e.type === "LINEITEM_COPIED":
          setTimeout(() => {
            focusCellByContent(props.tableId, e.payload.code)
          }, 500)
      }
    })

    const keySubscription = keyService.stream().subscribe(k => {
      switch(true) {
        case k.chord === chord.CTRL_SHIFT_PLUS && k.down === true:
          if(!window[`${props.tableId}_carousel_in_focus`]) {
            unfocusAllCells()
            lineItemsService.create(propertyService.getStoredPropertyId(), props.defaults, props.type)
          }
          break
        case k.chord === chord.CTRL_SHIFT_MINUS && k.down === true:
          if(!window[`${props.tableId}_carousel_in_focus`]) {
            const pager         = getPagingInfo(props.tableId);
            const currentRowNum = getFocusedRowIndex() + 1
            const currentRecordIndex = (pager.currentPage > 1 ? (pager.currentPage - 1) * pager.pageSize : 0) + currentRowNum - 1
            const records       = tableRef.current.props.value!;
            const currentRow    = records[currentRecordIndex];
            if (!currentRow.systeminput) showDeleteConfirmation({lineitemid: currentRow.lineitemid, propertyid: currentRow.propertyid, code: currentRow.code}, currentRecordIndex + 1)
          }
          break
      }
    })

    if(props.chartId && props.chartId !== -1) {
      detailAccountService.fetchDetailAccounts(props.chartId)
      summaryAccountService.fetchSummaryAccounts(props.chartId)
    }

    return function cleanup() {
      carouselSubscription.unsubscribe()
      detailAccountSubscription.unsubscribe()
      summaryAccountSubscription.unsubscribe()
      lineItemsEventSubscription.unsubscribe()
      keySubscription.unsubscribe()
    }
  }, [props.chartId])

  useEffect(() => {
    if (prevSearch !== "" && searchFilter === "") _.invoke(tableRef, 'current.filter', '', 'contains');
    else if (searchFilter !== "") _.invoke(tableRef, 'current.filter', searchFilter, 'contains');
    else return
  }, [searchFilter])

  useEffect(() => {
    !contextRow.systeminput || (contextRow.systeminput && !contextRow.systeminput.startsWith('system')) ? setMenu(nonSystemMenu) : setMenu(systemMenu)
  }, [contextRow])

  const persist = (value, field, rowData, event, sync=false) => {
    let lineItem = _.omit(_.cloneDeep(rowData), 'index') // strip fields added for table view

    if(field === "detailaccountid" && rowData.type === "hosp_dept_revenue" && !_.get(lineItem, "code") && !_.get(lineItem, "name")) {
      // set both name fields to the detail account's name when revenue is set
      let detail = _.find(details, (d) => d.detailaccountid === value)
      _.set(lineItem, "name", detail.description)
      _.set(lineItem, "code", detail.accountnumber)
      persist(detail.description, "name", findKinRecord(lineItem), event, true)
    } else if(field === "detailaccountid" && rowData.type === "hosp_dept_expense" && !_.get(lineItem, "code")) {
      // set code
      let detail = _.find(details, (d) => d.detailaccountid === value)
      _.set(lineItem, "code", detail.accountnumber)
    } else if(field === "detailaccountid" && (_.get(lineItem, "code") || _.get(lineItem, "name"))) {
      let detail = _.find(details, (d) => d.detailaccountid === value)
      setLineItemCache(lineItem)
      setDetailAccountCache(detail)
      setShowOverwriteModal(true)
    }

    if(field === "name") {
      // don't allow duplicate names
      const isDuplicate = _.chain(props.data).filter((i) => _.toLower(i.name) === _.toLower(value)).size().eq(2).value()
      if(isDuplicate && lineItem.name !== value && lineItem.name !== null) {
        notificationService.notify({
          type: "warn",
          message: "Departments must have unique names.",
          dismiss: 3000
        })
        return
      }
      // handle synchronizing parent/child names on name change
      if(!sync) {
        persist(value, field, findKinRecord(lineItem), event, true)
      }
    }

    // lineitemdetail passes the whole record back as the value because it
    // needs to update multiple database columns due to legacy schema design
    if(field === "lineitemdetail" && !_.isEqual(value, {state: "none"})) {
      lineItem = value
    } else {
      _.set(lineItem, field, value)
    }

    lineItemsService.patch(lineItem)
    if (value && field === "lineitemdetail") carouselService.updateRowData(lineItem);
  }

  useEffect(() => {
    if(canPaste(contextCell)) {
      nonSystemMenu.splice(1, 0, {label: 'Paste Cell', icon: 'pi pi-fw pi-file', command: e => { if(contextRow.systeminput) return;
        paste(contextCell, contextRow, lineItemsService.fetchLineItems, 'lineitem')
      }})
    }
    contextRow.systeminput === "systemImmutable" ? setMenu(systemMenu) : setMenu(nonSystemMenu)
  }, [contextCell])

  const overwriteCodeAndName = () => {
    const lineItem = _.cloneDeep(lineItemCache)
    const detailAccount = _.cloneDeep(detailAccountCache)
    if(lineItem.type === "hosp_dept_revenue") {
      _.set(lineItem, "code", detailAccount.accountnumber)
      _.set(lineItem, "name", detailAccount.description)
      persist(detailAccount.description, "name", findKinRecord(lineItem), {}, true)
    } else if(lineItem.type === "hosp_dept_expense") {
      _.set(lineItem, "code", detailAccount.accountnumber)
    }
    lineItemsService.patch(lineItem)
    setLineItemCache({})
    setDetailAccountCache({})
    setShowOverwriteModal(false)
  }

  const showDeleteConfirmation = (record: ContextRow, currentRow: number) => {
    automationService.broadcast("MODAL_OPENING", {})
    setDeleteConfirmation(Controls.DeleteConfirmation({
      show: true,
      close: () => {
        setDeleteConfirmation(null)
        setTimeout(() => automationService.broadcast("MODAL_CLOSING", {}), 200)
      },
      confirm: () => {
        lineItemsService.deleteOne(record.propertyid, record.lineitemid)
        setDeleteConfirmation(null)
        setTimeout(() => {
          focusFirstCellByRowIndex(props.tableId, currentRow - 2 > 0 ? currentRow - 2 : 0)
        }, 200)
      },
      rowNum: currentRow,
      code: record.code
    }))
  }

  const showDuplicateConfirmation = (copy: (n: number) => Promise<void>) => {
    setDuplicateConfirmation(
      <DuplicateModal
        close={() => {setDuplicateConfirmation(null); focusCellByDataId( props.tableId, contextRow.lineitemid)}}
        copy={copy}
        show={true}
        title={contextRow.code}
      />
    )
  }

  function updateContextMenu(originalEvent){
    let systemRows = document.querySelectorAll('.system-row, .systemImmutable-row');
    for (let i of systemRows){
      if (originalEvent.target === i || i.contains(originalEvent.target)) {
        setMenu(systemMenu);
      } else {
        if (!menu.length){
          setMenu(nonSystemMenu)
        }
      }
    }
    _.invoke(menuRef.current, "show", originalEvent)
  }

  const rowClassName = (rowData) => {
    if (rowData.systeminput && rowData.systeminput === 'system'){
      return {'system-row' : rowData.systeminput === 'system'}
    }
    else if (rowData.systeminput && rowData.systeminput === 'systemImmutable'){
      return {'systemImmutable-row' : rowData.systeminput === 'systemImmutable'}
    }
    else return {'' : rowData}
  }

  return (
    <div>
      <SearchHeader setSearchFilter={setSearchFilter} />
      <ContextMenu model={menu} ref={menuRef} appendTo={document.querySelector("body")} onHide={() => {}}/>
      {showOverwriteModal &&
        <ConfirmationModal
          show={showOverwriteModal}
          title={'Overwrite Code and Name'}
          confirmText="Yes"
          cancelText="No"
          message={"Would you like to overwrite existing code and department name?"}
          confirmFunc={() => overwriteCodeAndName()}
          cancelFunc={() => setShowOverwriteModal(false)}
          toggleShow={() => setShowOverwriteModal(false)}
          confirmOnly={false}
          closeOnly={false}/>
      }
      { defaults &&
      <DepartmentalLineItemsTable_s id={`${props.tableId}-scrollable-container`}>
        {inflationData.length > 0 && areaMeasuresData.length > 0 && lineItemsData && systemLineItemsData &&
        <DataTable
          globalFilter={searchFilter}
          id={props.tableId}
          ref={tableRef}
          value={props.data}
          paginator={true}
          rows={20}
          rowsPerPageOptions={[10, 20, 50, 100, 500]}
          scrollable={true}
          frozenWidth="45px"
          paginatorPosition="bottom"
          rowClassName={rowClassName}
          onContextMenu={e => menuRef.current ? updateContextMenu(e.originalEvent) : null}
          contextMenuSelection={contextRow}
          onContextMenuSelectionChange={e => {
            setContextRow(e.value)
            setContextCell(e.originalEvent.target)
          }}
          stateStorage="custom"
          customSaveState={state => localStorage.setItem("primereact-table-preferences", JSON.stringify({rows: state.rows}))}
          customRestoreState={() => JSON.parse(localStorage.getItem("primereact-table-preferences")!)}
        >
          <Column header="#" frozen={true} style={{width: "45px"}}
            body={(rowData, props) => props.rowIndex + 1}
          />
          <Column style={{width: "100px"}} className="p-editable-column"
            header="Cash Flow?"
            {...field('reportstatus', (rowData, props) => CheckboxInput({persist, ...props}))}
            editor={props => CheckboxInput({persist, ...props})}
          />
          <Column style={{width: "160px"}} className="immutable"
            filter
            header="Line Item Type"
            {...field('type', (rowData, props) => DropdownField({options: LineItemTypeOptions, default: "expense", rowData, persist, ...props}))}
          />
          <Column style={{width: "325px"}} className="p-editable-column"
            header="Account Number"
            sortable
            {...field('detailaccountid', (rowData, props) => DropdownField({options: unfilteredDetailAccountOptions, rowData, ...props}))}
            editor={props => <DropdownEdit {...props} options={filterDetailAccounts(props.rowData)} default="" persist={persist} filter={true} />}
          />
          <Column style={{width: "200px"}} className="p-editable-colum system-immutable-editable"
            header="Code"
            {...field('code')}
            editor={props => TextEdit({persist, ...props})}
          />
          <Column style={{width: "200px"}} className="p-editable-column system-disabled"
            header="Department Name"
            {...field('name')}
            editor={props => TextEdit({persist, ...props})}
          />
          <Column style={{width: "250px"}} className="p-editable-column"
            header="Input Type"
            {...field('lineitemdetail', (rowData, props) =>
              <CarouselTrigger field="lineitemdetail" title="Input Type" rowData={rowData} persist={persist} defaults={defaults}
                controls={detail_controls}
                componentAtRest={Controls.InputTypeAtRest}
                hover={detail_hovers}
                namedItems={{
                  inflation: inflationData,
                  areaMeasures: areaMeasuresData,
                  lineItems: lineItemsData,
                  systemLineItems: systemLineItemsData,
                  rentroll: rentrollData,
                  valuationItems: _.get(propertyService.get(), 'valuationdetail', []),
                  LineItemTypeOptions
                }}
              />,
              (err) => _.includes(['amt', 'amttype', 'areaid', 'lineitemdetail', 'inflationdetail', 'inputtype'], err.path[0]))
            }
          />
        </DataTable> }
        { carouselData && carouselData['show'] && <CarouselModal code="code" data={carouselData} key="carousel-modal" width={887} /> }
        { deleteConfirmation }
        { duplicateConfirmation }
        { inflationData.length > 0 && areaMeasuresData.length > 0 && lineItemsData && systemLineItemsData && props.data.length ? <TableAutomation tableId={props.tableId}/> : null }
      </DepartmentalLineItemsTable_s>
      }
    </div>
  )
}

export default ChartDepartmentalLineItemsTable
